import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Twitter, Instagram, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useState } from 'react';
import { toast } from 'sonner';
import { insertData, isSupabaseConfigured } from '../../lib/supabase';

const footerLinks = {
  services: [
    { name: 'Création de site internet', href: '/services/site-internet' },
    { name: 'Développement d’applications', href: '/services/applications' },
    { name: 'Création e-commerce', href: '/services/e-commerce' },
    { name: 'SEO / Référencement', href: '/services/seo' },
    { name: 'Maintenance web', href: '/services/maintenance' },
    { name: 'Branding & identité', href: '/services/branding' },
  ],
  company: [
    { name: 'À propos', href: '/a-propos' },
    { name: 'Réalisations', href: '/realisations' },
    { name: 'Tarifs', href: '/tarifs' },
    { name: 'Blog', href: '/blog' },
    { name: 'Contact', href: '/contact' },
    { name: 'FAQ', href: '/faq' },
    { name: 'Demande de devis', href: '/devis' },
  ],
  legal: [
    { name: 'Mentions légales', href: '/mentions-legales' },
    { name: 'Politique de confidentialité', href: '/confidentialite' },
    { name: 'Politique de cookies', href: '/cookies' },
    { name: 'Licences', href: '/licences' },
  ],
};

export const Footer = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleNewsletter = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error('Veuillez entrer votre email');
      return;
    }

    setIsLoading(true);

    try {
      const { error, demo } = await insertData('newsletter_subscribers', {
        email,
        subscribed_at: new Date().toISOString(),
      });

      if (error) {
        throw error;
      }

      if (demo) {
        toast.success('Merci ! (Mode démo - Supabase non configuré)');
      } else {
        toast.success('Merci pour votre inscription !');
      }

      setEmail('');
    } catch (err) {
      toast.error('Une erreur est survenue. Réessayez plus tard.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <footer data-testid="footer" className="bg-card border-t border-border" role="contentinfo">
      {/* Newsletter Section */}
      <div className="weblax-container py-12 md:py-16 border-b border-border">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="weblax-heading-2 mb-4">Restez informé</h2>
          <p className="weblax-body-lg mb-6">
            Recevez nos conseils, actualités et offres exclusives directement dans votre boîte mail.
          </p>

          <form
            onSubmit={handleNewsletter}
            className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          >
            <label htmlFor="newsletter-email" className="sr-only">
              Votre adresse email
            </label>
            <Input
              id="newsletter-email"
              type="email"
              placeholder="Votre email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="newsletter-email"
              className="flex-1 weblax-input"
              autoComplete="email"
            />
            <Button
              type="submit"
              data-testid="newsletter-submit"
              disabled={isLoading}
              className="weblax-btn-primary whitespace-nowrap"
            >
              {isLoading ? 'Envoi...' : "S'inscrire"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>

          {!isSupabaseConfigured && (
            <p className="text-xs text-muted-foreground mt-2">(Mode démo)</p>
          )}
        </div>
      </div>

      {/* Main Footer */}
      <div className="weblax-container py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-4" aria-label="Retour à l’accueil Weblax">
              <img
                src="/images/logo-weblax.png"
                alt="Logo Weblax"
                className="w-10 h-10 object-contain"
              />
              <span className="font-bold text-xl">Weblax</span>
            </Link>

            <p className="weblax-body mb-6 max-w-sm">
              Performance. Créativité. Simplicité.
              <br />
              Votre partenaire digital pour la création de site internet, le SEO,
              le e-commerce et les solutions web sur mesure.
            </p>

            <address className="not-italic space-y-3">
              <a
                href="mailto:contact@weblax.fr"
                className="flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors"
              >
                <Mail className="w-5 h-5" />
                contact@weblax.fr
              </a>

              <a
                href="tel:+33685802016"
                className="flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors"
              >
                <Phone className="w-5 h-5" />
                +33 6 85 80 20 16
              </a>

              <div className="flex items-center gap-3 text-muted-foreground">
                <MapPin className="w-5 h-5" />
                Paris, France
              </div>
            </address>
          </div>

          {/* Services */}
          <nav aria-label="Liens vers les services">
            <h3 className="font-semibold mb-4">Services</h3>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Company */}
          <nav aria-label="Liens vers les pages de l’entreprise">
            <h3 className="font-semibold mb-4">Entreprise</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Legal */}
          <nav aria-label="Liens légaux">
            <h3 className="font-semibold mb-4">Légal</h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-border">
        <div className="weblax-container py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Weblax. Tous droits réservés.
            </p>

            <div className="flex items-center gap-4">
              <a
                href="https://instagram.com/weblax.fr"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="Instagram Weblax"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;